// src/components/FeatureHelpManager.js
// 管理画面：機能画面ごとの説明PDFを登録・差し替え・有効/無効にする。
import React, { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "../services/api";
import "./FeatureHelpManager.css";

// 社員側の主な画面（feature_key は URL から導出されるキー）
const KNOWN_SCREENS = [
  { key: "worktime", label: "実績（打刻）" },
  { key: "worktime/record", label: "実績（記録）" },
  { key: "schedule", label: "スケジュール登録" },
  { key: "leave", label: "有給" },
  { key: "applications", label: "申請" },
  { key: "events", label: "イベント一覧" },
  { key: "event", label: "イベント詳細" },
  { key: "salary", label: "給与明細" },
  { key: "notice", label: "お知らせ" },
  { key: "emergency_contact", label: "緊急連絡網" },
  { key: "bcp", label: "BCP" },
  { key: "stress", label: "ストレスチェック" },
  { key: "card_front", label: "社員証（表）" },
  { key: "card_back", label: "社員証（裏）" },
];

export default function FeatureHelpManager() {
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [msg, setMsg] = useState("");
  const [form, setForm] = useState({ feature_key: "", title: "", pdf: null });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get("/api/admin/feature-help/");
      setItems(Array.isArray(res.data) ? res.data : res.data?.results || []);
    } catch {
      setMsg("一覧の取得に失敗しました。");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const submit = async (e) => {
    e.preventDefault();
    const key = (form.feature_key || "").trim().replace(/^\/+|\/+$/g, "");
    if (!key) { setMsg("機能キーを選択してください。"); return; }
    if (!form.title.trim()) { setMsg("タイトルを入力してください。"); return; }

    const existing = items.find((x) => x.feature_key === key);
    if (!existing && !form.pdf) { setMsg("新規登録にはPDFファイルが必要です。"); return; }

    const fd = new FormData();
    fd.append("feature_key", key);
    fd.append("title", form.title.trim());
    if (form.pdf) fd.append("pdf_file", form.pdf);

    setMsg("");
    try {
      if (existing) {
        await api.patch(`/api/admin/feature-help/${existing.id}/`, fd, {
          headers: { "Content-Type": "multipart/form-data" },
        });
        setMsg("説明PDFを差し替えました。");
      } else {
        await api.post("/api/admin/feature-help/", fd, {
          headers: { "Content-Type": "multipart/form-data" },
        });
        setMsg("説明PDFを登録しました。");
      }
      setForm({ feature_key: "", title: "", pdf: null });
      e.target.reset();
      load();
    } catch (err) {
      const d = err?.response?.data;
      setMsg(`保存に失敗しました。${d ? JSON.stringify(d) : ""}`);
    }
  };

  const toggleActive = async (item) => {
    try {
      await api.patch(`/api/admin/feature-help/${item.id}/`, { is_active: !item.is_active });
      load();
    } catch {
      setMsg("有効/無効の切替に失敗しました。");
    }
  };

  const remove = async (item) => {
    if (!window.confirm(`「${item.title}」を削除しますか？`)) return;
    try {
      await api.delete(`/api/admin/feature-help/${item.id}/`);
      load();
    } catch {
      setMsg("削除に失敗しました。");
    }
  };

  const labelOf = (key) => KNOWN_SCREENS.find((s) => s.key === key)?.label || key;

  return (
    <div className="fh-container">
      <header className="fh-header">
        <h1 className="fh-title">説明PDF管理</h1>
        <button className="fh-btn-back" onClick={() => navigate(-1)}>戻る</button>
      </header>

      <p className="fh-note">
        画面ごとに説明PDFを登録します。登録した画面だけ、社員側の右上に「説明使用／説明不要」が表示されます。
        同じ機能キーで再登録すると<strong>PDFは上書き</strong>されます。
      </p>

      {msg && <div className="fh-msg">{msg}</div>}

      <form className="fh-form" onSubmit={submit}>
        <div className="fh-form-row">
          <label htmlFor="fh-key">対象画面</label>
          <select
            id="fh-key"
            value={form.feature_key}
            onChange={(e) => {
              const key = e.target.value;
              const cur = items.find((x) => x.feature_key === key);
              setForm((p) => ({
                ...p,
                feature_key: key,
                title: cur?.title || (key ? `${labelOf(key)}の使い方` : ""),
              }));
            }}
          >
            <option value="">— 選択してください —</option>
            {KNOWN_SCREENS.map((s) => (
              <option key={s.key} value={s.key}>
                {s.label}（{s.key}）{items.some((x) => x.feature_key === s.key) ? " ✓登録済" : ""}
              </option>
            ))}
          </select>
        </div>

        <div className="fh-form-row">
          <label htmlFor="fh-title">タイトル</label>
          <input
            id="fh-title"
            type="text"
            value={form.title}
            placeholder="例: 実績の使い方"
            onChange={(e) => setForm((p) => ({ ...p, title: e.target.value }))}
          />
        </div>

        <div className="fh-form-row">
          <label htmlFor="fh-pdf">PDFファイル</label>
          <input
            id="fh-pdf"
            type="file"
            accept="application/pdf"
            onChange={(e) => setForm((p) => ({ ...p, pdf: e.target.files?.[0] || null }))}
          />
        </div>

        <button type="submit" className="fh-btn-primary">登録 / 差し替え</button>
      </form>

      <h2 className="fh-subtitle">登録済みの説明PDF</h2>
      {loading ? (
        <div className="fh-info">読み込み中…</div>
      ) : items.length === 0 ? (
        <div className="fh-info">まだ登録されていません。</div>
      ) : (
        <table className="fh-table">
          <thead>
            <tr>
              <th>対象画面</th>
              <th>タイトル</th>
              <th>PDF</th>
              <th>状態</th>
              <th>操作</th>
            </tr>
          </thead>
          <tbody>
            {items.map((it) => (
              <tr key={it.id} className={it.is_active ? "" : "fh-row-off"}>
                <td>{labelOf(it.feature_key)}<br /><small>{it.feature_key}</small></td>
                <td>{it.title}</td>
                <td>
                  {it.pdf_url ? (
                    <a href={it.pdf_url} target="_blank" rel="noopener noreferrer">表示</a>
                  ) : "—"}
                </td>
                <td>{it.is_active ? "有効" : "無効"}</td>
                <td className="fh-actions">
                  <button onClick={() => toggleActive(it)}>{it.is_active ? "無効化" : "有効化"}</button>
                  <button className="fh-btn-danger" onClick={() => remove(it)}>削除</button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}
